import { movies } from "@/mocks/movies";
import { tvShows } from "@/mocks/tv";
import type { MediaItem } from "@/types/media";

export const allMedia: MediaItem[] = [
  ...(movies as unknown as MediaItem[]),
  ...(tvShows as unknown as MediaItem[]),
];

export const movieList: MediaItem[] = movies as unknown as MediaItem[];
export const tvList: MediaItem[] = tvShows as unknown as MediaItem[];

export function getMediaById(id: string | undefined): MediaItem | undefined {
  if (!id) return undefined;
  return allMedia.find((item) => item.id === id);
}

export function getMediaTitle(id: string): string {
  return getMediaById(id)?.title ?? "";
}

export function searchMedia(
  keyword: string,
  type: "all" | "movie" | "tv" = "all",
): MediaItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allMedia.filter((item) => {
    if (type !== "all" && item.type !== type) return false;
    const haystack = [
      item.title,
      item.director,
      ...item.actors,
      ...item.genres,
      ...item.tags,
      item.region,
      String(item.year),
      item.synopsis,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelatedMedia(item: MediaItem, limit = 5): MediaItem[] {
  return allMedia
    .filter((other) => other.id !== item.id)
    .map((other) => {
      let score = 0;
      if (other.type === item.type) score += 1;
      if (other.director === item.director) score += 3;
      score += other.genres.filter((g) => item.genres.includes(g)).length * 2;
      score += other.tags.filter((t) => item.tags.includes(t)).length;
      return { other, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.other);
}

export function collectGenres(list: MediaItem[]): string[] {
  const set = new Set<string>();
  list.forEach((item) => item.genres.forEach((g) => set.add(g)));
  return Array.from(set);
}

export function collectRegions(list: MediaItem[]): string[] {
  const set = new Set<string>();
  list.forEach((item) => set.add(item.region));
  return Array.from(set);
}

export function collectYears(list: MediaItem[]): number[] {
  const set = new Set<number>();
  list.forEach((item) => set.add(item.year));
  return Array.from(set).sort((a, b) => b - a);
}

export interface FilterOptions {
  genres: string[];
  years: string[];
  region: string[];
  episodes: string[];
  sort: "latest" | "rating" | "hot";
}

export function applyFilters(
  list: MediaItem[],
  options: FilterOptions,
): MediaItem[] {
  const result = list.filter((item) => {
    if (options.genres.length > 0 && !item.genres.some((g) => options.genres.includes(g))) {
      return false;
    }
    if (options.years.length > 0 && !options.years.includes(String(item.year))) {
      return false;
    }
    if (options.region.length > 0 && !options.region.includes(item.region)) {
      return false;
    }
    if (options.episodes.length > 0) {
      const count = item.episodes ?? 0;
      const matched = options.episodes.some((range) => {
        if (range === "short") return count <= 20;
        if (range === "medium") return count > 20 && count <= 40;
        if (range === "long") return count > 40;
        return false;
      });
      if (!matched) return false;
    }
    return true;
  });

  return [...result].sort((a, b) => {
    if (options.sort === "rating") return b.rating - a.rating;
    if (options.sort === "hot") {
      return b.rating * 1000 + b.year - (a.rating * 1000 + a.year);
    }
    return b.year - a.year;
  });
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    currentPage: safePage,
    total: list.length,
  };
}